import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { supabase } from '../../lib/supabase';
import { setPendingPromptData } from '../../store/promptSlice';
import { analyzeScenario } from '../../services/apiService';

import HeyOpening from '../HeyOpening';
import KuraOpening from '../KuraOpening';
import InitialPrompt from '../InitialPrompt'; // Modified version
import DetailForm from '../DetailForm'; // New component
import UserSettingsModal from '../UserSettingsModal';
import UserSettingsModalMobile from '../UserSettingsModalMobile';

const defaultFields = [
  { id: 'colors', label: 'Colors', type: 'text', placeholder: 'e.g. Blue, Gold, Modern tones' },
  { id: 'mood', label: 'Mood / Style', type: 'text', placeholder: 'e.g. Modern, Cozy, Minimalist' }
];

function HomePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [showUserSettingsModal, setShowUserSettingsModal] = useState(false);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Animation states
  const [showHeyOpening, setShowHeyOpening] = useState(true);
  const [showKuraOpening, setShowKuraOpening] = useState(false);

  // Logic states
  const [step, setStep] = useState('animation'); // animation -> prompt -> details
  const [basicData, setBasicData] = useState(null); // { scenario }
  const [dynamicFields, setDynamicFields] = useState([]);

  useEffect(() => {
    if (showHeyOpening) {
      const timer = setTimeout(() => {
        setShowKuraOpening(true);
      }, 1700);

      return () => clearTimeout(timer);
    }
  }, [showHeyOpening]);

  const handleHeyAnimationComplete = () => {
    setShowHeyOpening(false);
  };

  const handleKuraAnimationComplete = () => {
    setShowKuraOpening(false);
    setStep('prompt');
  };

  // Get current user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
    };
    getUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Step 1: User enters Project Name & Scenario
  const handleInitialNext = async (data) => {
    setBasicData(data);

    // Call backend to get dynamic fields
    try {
      const result = await analyzeScenario(data.scenario);
      if (result && result.fields && result.fields.length > 0) {
        setDynamicFields(result.fields);
      } else {
        // If no fields returned, use default fields
        setDynamicFields(defaultFields);
      }
    } catch (error) {
      console.error("Failed to analyze scenario:", error);
      // Fallback: use default fields if API fails
      setDynamicFields(defaultFields);
    }
    // Always go to details page
    setStep('details');
  };

  // Step 2: User fills details or skips
  const handleDetailsSubmit = (details) => {
    handleGenerationTrigger(basicData, details);
  };

  const handleDetailsSkip = () => {
    handleGenerationTrigger(basicData, {});
  };

  const isValidCategoryField = (field) => {
    return field.type === 'smart_dropdown' &&
      field.options?.length > 0 &&
      !field.id.toLowerCase().includes('color') &&
      !field.id.toLowerCase().includes('typography');
  };

  // Helper function to convert string to camelCase
  const toCamelCase = (str) => {
    if (!str) return '';
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
      })
      .replace(/\s+/g, '')
      .replace(/[^a-zA-Z0-9]/g, '');
  };

  const handleGenerationTrigger = async (basic, details) => {
    const specificFields = ['for', 'color', 'typography', 'size', 'mood', 'layout', 'material', 'style', 'assets'];
    const formattedDetails = {};
    const dynamicInput = {};
    const generatedCategories = [];

    // Format array fields - they should already be arrays from DetailForm
    specificFields.forEach(fieldKey => {
      const value = details[fieldKey];
      if (value !== undefined && value !== null) {
        formattedDetails[fieldKey] = value;
      }
    });

    dynamicFields.forEach(field => {
      const fieldValue = details[field.id];
      const isSpecificField = specificFields.includes(field.id);

      // Convert dynamic field to dynamicInput format (camelCase from display)
      if (!isSpecificField && field.display && fieldValue) {
        dynamicInput[toCamelCase(field.display)] = fieldValue;
      }

      // Generate availableTags from unselected options
      if (isValidCategoryField(field) && field.options?.length > 0) {
        const valueSet = new Set(Array.isArray(fieldValue) ? fieldValue : fieldValue ? [fieldValue] : []);
        field.options.forEach(option => {
          if (!valueSet.has(option)) generatedCategories.push(option);
        });
      }
    });

    const fullData = {
      idea: basic.scenario,
      fields: dynamicFields,
      availableTags: generatedCategories,
      dynamicInput: Object.keys(dynamicInput).length > 0 ? dynamicInput : null,
      ...formattedDetails
    };

    dispatch(setPendingPromptData(fullData));

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      navigate('/result');
    } else {
      navigate('/auth');
    }
  };

  return (
    <div className="App">
      {/* User Profile */}
      {user && !showHeyOpening && !showKuraOpening && (
        <div
          style={{
            position: 'fixed',
            top: isMobile ? '1.5rem' : '3rem',
            right: isMobile ? '1.5rem' : '3rem',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 1000,
          }}
          onClick={() => setShowUserSettingsModal(true)}
        >
          <img
            src="/user-profile.png"
            alt="User Profile"
            style={{ width: '100%', height: '100%', borderRadius: '50%' }}
          />
        </div>
      )}

      {showHeyOpening && <HeyOpening onAnimationComplete={handleHeyAnimationComplete} />}
      {showKuraOpening && <KuraOpening onAnimationComplete={handleKuraAnimationComplete} />}

      {!showHeyOpening && !showKuraOpening && step === 'prompt' && (
        <InitialPrompt
          onNext={handleInitialNext}
        />
      )}

      {!showHeyOpening && !showKuraOpening && step === 'details' && (
        <DetailForm
          fields={dynamicFields}
          scenario={basicData?.scenario}
          onGenerate={handleDetailsSubmit}
          onSkip={handleDetailsSkip}
        />
      )}

      {showUserSettingsModal && (
        isMobile ? (
          <UserSettingsModalMobile
            user={user}
            onClose={() => setShowUserSettingsModal(false)}
          />
        ) : (
          <UserSettingsModal
            user={user}
            onClose={() => setShowUserSettingsModal(false)}
          />
        )
      )}
    </div>
  );
}

export default HomePage;
